/**
 * ARIA chat P2/P3 API — chat, execute-action, transcribe.
 */
import { apiFetch, resolveApiUrl, getToken } from './client'

export interface AriaChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface AriaChatMoney {
  amount: number
  formatted: string
}

export interface AriaChatDigestProject {
  project_id?: number | null
  project_name?: string | null
  outstanding?: AriaChatMoney | null
  received?: AriaChatMoney | null
  retainage?: AriaChatMoney | null
  left_to_bill?: AriaChatMoney | null
  open_count?: number | null
  routes?: {
    hub?: string
    outstanding?: string
    lien?: string
  } | null
}

export interface AriaCashForecastWeek {
  week_start: string
  in?: AriaChatMoney | null
  subs_out?: AriaChatMoney | null
  other_out?: AriaChatMoney | null
  payroll?: AriaChatMoney | null
  overhead?: AriaChatMoney | null
  close?: AriaChatMoney | null
}

export interface AriaCashForecastCollectNow {
  pay_app_id?: number | null
  project_id?: number | null
  project_name?: string | null
  amount?: AriaChatMoney | null
  days_overdue?: number | null
}

export interface AriaCashForecastDigest {
  kind: 'cash_forecast'
  bank?: AriaChatMoney | null
  bank_as_of?: string | null
  bank_stale?: boolean
  weekly_payroll?: AriaChatMoney | null
  monthly_overhead?: AriaChatMoney | null
  overhead_note?: string | null
  weeks?: AriaCashForecastWeek[]
  verdict?: {
    status: 'unknown' | 'short' | 'positive'
    first_short_week?: string | null
    shortfall?: AriaChatMoney | null
    lowest_point?: AriaChatMoney | null
    lowest_week?: string | null
    lowest_line?: string | null
  } | null
  collect_now?: AriaCashForecastCollectNow[]
  collect_now_total?: AriaChatMoney | null
  weeks_requested?: number | null
  week_label?: string | null
  owner_behavior?: {
    owner_name?: string | null
    n_paid?: number | null
    median_days?: number | null
    p25_days?: number | null
    p75_days?: number | null
    confidence_line?: string | null
    range_line?: string | null
    tier?: string | null
  }[]
  strip?: {
    cash_next_30_days?: AriaChatMoney | null
    expected_collections?: AriaChatMoney | null
    lowest_projected_balance?: AriaChatMoney | null
    lowest_week?: string | null
    lowest_line?: string | null
  } | null
}

export interface AriaOwnerPayBehaviorDigest {
  kind: 'owner_pay_behavior'
  owner_name?: string | null
  n_paid?: number | null
  median_days?: number | null
  p25_days?: number | null
  p75_days?: number | null
  confidence_line?: string | null
  range_line?: string | null
  tier?: string | null
  terms_contrast?: string | null
}

export interface AriaOwnerCardDigest {
  kind: 'owner_card'
  project_id?: number | null
  project_name?: string | null
  project_number?: string | null
  owner_id?: number | null
  owner_name?: string | null
  contact_names?: string | null
  phone?: string | null
  email?: string | null
  email_pending?: boolean
  address?: string | null
  default_terms?: string | null
  pays_by?: string | null
  pay_behavior_line?: string | null
}

export interface AriaMoneyOnTableLine {
  kind?: string | null
  label?: string | null
  amount?: AriaChatMoney | null
  href?: string | null
  release_date?: string | null
  summed?: boolean
  pct?: number | null
  stated_at?: string | null
}

export interface AriaMoneyOnTableProject {
  project_id?: number | null
  project_name?: string | null
  lines?: AriaMoneyOnTableLine[]
  estimate_line?: AriaMoneyOnTableLine | null
  total_facts?: AriaChatMoney | null
  total_label?: string | null
}

export interface AriaMoneyOnTableDigest {
  kind: 'money_on_table'
  projects?: AriaMoneyOnTableProject[]
  total_facts?: AriaChatMoney | null
  total_label?: string | null
  estimate_excluded_from_total?: boolean
}

export interface AriaPendingUploadRow {
  upload_id?: number | null
  project_id?: number | null
  project_name?: string | null
  vendor_company?: string | null
  doc_type?: string | null
  amount?: AriaChatMoney | null
  uploaded_at?: string | null
  age_label?: string | null
  filed_by?: string | null
}

export interface AriaPendingByProject {
  project_id?: number | null
  project_name?: string | null
  count?: number | null
}

export interface AriaPendingSubUploadsDigest {
  kind: 'pending_sub_uploads'
  count?: number | null
  uploads?: AriaPendingUploadRow[]
  by_project?: AriaPendingByProject[]
}

export interface AriaChangeOrderRow {
  id?: number | null
  number?: number | string | null
  title?: string | null
  amount?: AriaChatMoney | null
  status?: string | null
  approved_at?: string | null
}

export interface AriaChangeOrdersDigest {
  kind: 'change_orders'
  project_id?: number | null
  project_name?: string | null
  original_contract?: AriaChatMoney | null
  approved_total?: AriaChatMoney | null
  pending_total?: AriaChatMoney | null
  adjusted_contract?: AriaChatMoney | null
  billed_to_date?: AriaChatMoney | null
  billed_pct_of_adjusted?: number | null
  change_orders?: AriaChangeOrderRow[]
  count?: number | null
}

export interface AriaLienWaiverRow {
  pay_app_id?: number | null
  pay_app_number?: number | null
  amount?: AriaChatMoney | null
  conditional?: boolean | null
  unconditional?: boolean | null
  conditional_signed_at?: string | null
  unconditional_signed_at?: string | null
}

export interface AriaLienWaiversDigest {
  kind: 'lien_waivers'
  project_id?: number | null
  project_name?: string | null
  pay_apps?: AriaLienWaiverRow[]
  count?: number | null
}

export interface AriaSubLedgerRow {
  vendor_name?: string | null
  invoiced?: AriaChatMoney | null
  approved?: AriaChatMoney | null
  paid?: AriaChatMoney | null
  owed?: AriaChatMoney | null
  last_paid_at?: string | null
}

export interface AriaSubLedgerDigest {
  kind: 'sub_ledger'
  project_id?: number | null
  project_name?: string | null
  subs?: AriaSubLedgerRow[]
  count?: number | null
}

export interface AriaSubCostsProjectRow {
  project_id?: number | null
  project_name?: string | null
  invoiced?: AriaChatMoney | null
  approved?: AriaChatMoney | null
  paid?: AriaChatMoney | null
  owed?: AriaChatMoney | null
  last_paid_at?: string | null
  href?: string | null
}

export interface AriaSubCostsRow {
  vendor_name?: string | null
  invoiced?: AriaChatMoney | null
  approved?: AriaChatMoney | null
  paid?: AriaChatMoney | null
  owed?: AriaChatMoney | null
  last_paid_at?: string | null
  projects?: AriaSubCostsProjectRow[]
}

export interface AriaSubCostsDigest {
  kind: 'sub_costs'
  project_id?: number | null
  project_name?: string | null
  subs?: AriaSubCostsRow[]
  totals?: {
    invoiced?: AriaChatMoney | null
    approved?: AriaChatMoney | null
    paid?: AriaChatMoney | null
    owed?: AriaChatMoney | null
  } | null
  count?: number | null
}

export interface AriaSovLineRow {
  item?: string | null
  description?: string | null
  scheduled_value?: AriaChatMoney | null
  billed_to_date?: AriaChatMoney | null
  pct_complete?: number | null
  balance?: AriaChatMoney | null
}

export interface AriaSovLinesDigest {
  kind: 'sov_lines'
  project_id?: number | null
  project_name?: string | null
  pay_app_number?: number | null
  lines?: AriaSovLineRow[]
  count?: number | null
}

export interface AriaPayAppPaymentRow {
  id?: number | null
  source?: string | null
  amount?: AriaChatMoney | null
  method?: string | null
  paid_at?: string | null
  status?: string | null
}

export interface AriaPayAppPaymentsDigest {
  kind: 'pay_app_payments'
  project_id?: number | null
  project_name?: string | null
  pay_app_id?: number | null
  pay_app_number?: number | null
  amount_due?: AriaChatMoney | null
  payments?: AriaPayAppPaymentRow[]
  total_paid?: AriaChatMoney | null
  balance?: AriaChatMoney | null
  count?: number | null
}

export interface AriaChatDigest {
  kind?:
    | 'cash_forecast'
    | 'owner_pay_behavior'
    | 'owner_card'
    | 'money_on_table'
    | 'pending_sub_uploads'
    | 'change_orders'
    | 'lien_waivers'
    | 'sub_ledger'
    | 'sub_costs'
    | 'sov_lines'
    | 'pay_app_payments'
    | 'project_financials'
    | string
  projects?: AriaChatDigestProject[] | AriaMoneyOnTableProject[]
  total?: {
    outstanding?: AriaChatMoney | null
    received?: AriaChatMoney | null
    retainage?: AriaChatMoney | null
    left_to_bill?: AriaChatMoney | null
  } | null
  totals?: {
    invoiced?: AriaChatMoney | null
    approved?: AriaChatMoney | null
    paid?: AriaChatMoney | null
    owed?: AriaChatMoney | null
  } | null
  project_id?: number | null
  project_name?: string | null
  outstanding?: AriaChatMoney | null
  received?: AriaChatMoney | null
  retainage?: AriaChatMoney | null
  left_to_bill?: AriaChatMoney | null
  routes?: AriaChatDigestProject['routes']
  /** Present when kind === 'cash_forecast' (same shape as AriaCashForecastDigest). */
  bank?: AriaChatMoney | null
  bank_as_of?: string | null
  bank_stale?: boolean
  weekly_payroll?: AriaChatMoney | null
  monthly_overhead?: AriaChatMoney | null
  overhead_note?: string | null
  weeks?: AriaCashForecastWeek[]
  verdict?: AriaCashForecastDigest['verdict']
  collect_now?: AriaCashForecastCollectNow[]
  collect_now_total?: AriaChatMoney | null
  weeks_requested?: number | null
  week_label?: string | null
  owner_behavior?: AriaCashForecastDigest['owner_behavior']
  strip?: AriaCashForecastDigest['strip']
  /** owner_pay_behavior */
  confidence_line?: string | null
  range_line?: string | null
  tier?: string | null
  terms_contrast?: string | null
  n_paid?: number | null
  median_days?: number | null
  p25_days?: number | null
  p75_days?: number | null
  /** money_on_table */
  total_facts?: AriaChatMoney | null
  total_label?: string | null
  estimate_excluded_from_total?: boolean
  /** Present when kind === 'pending_sub_uploads'. */
  count?: number | null
  uploads?: AriaPendingUploadRow[]
  by_project?: AriaPendingByProject[]
  /** Accounting digests */
  original_contract?: AriaChatMoney | null
  approved_total?: AriaChatMoney | null
  pending_total?: AriaChatMoney | null
  adjusted_contract?: AriaChatMoney | null
  billed_to_date?: AriaChatMoney | null
  billed_pct_of_adjusted?: number | null
  change_orders?: AriaChangeOrderRow[]
  pay_apps?: AriaLienWaiverRow[]
  subs?: AriaSubLedgerRow[] | AriaSubCostsRow[]
  lines?: AriaSovLineRow[]
  pay_app_id?: number | null
  pay_app_number?: number | null
  amount_due?: AriaChatMoney | null
  payments?: AriaPayAppPaymentRow[]
  total_paid?: AriaChatMoney | null
  balance?: AriaChatMoney | null
}

export interface AriaChatLink {
  label: string
  href: string
}

export interface AriaChatSuggestion {
  label: string
  send: string
  project_id?: number | null
  project_name?: string | null
}

export interface AriaChatAction {
  action_id: number
  action_type: string
  params?: Record<string, unknown>
  preview?: Record<string, unknown>
  expires_at?: string
  status?: string
}

export interface AriaChatReply {
  role: 'assistant'
  content: string
  tool_activity?: string[]
  digest?: AriaChatDigest | null
  links?: AriaChatLink[]
  suggestions?: AriaChatSuggestion[]
  actions?: AriaChatAction[]
  missing_capability?: boolean
  tool_calls_used?: number
  loop_guard_tripped?: boolean
}

export interface AriaExecuteResult {
  ok: boolean
  action_type?: string
  result?: {
    message?: string
    download?: { filename: string; content_type: string; base64: string }
    download_url?: string
    filename?: string
    digest?: unknown
    sent?: boolean
    to?: string
  }
  message?: string
}

export interface AriaTranscribeResult {
  text: string
  cost_estimate?: {
    usd: number
    duration_sec: number
    model: string
    rate_usd_per_min: number
  }
  vocab_boosted?: boolean
}

export const ARIA_CHAT_MAX_TURNS = 20

export interface AriaChatContext {
  route?: string | null
  has_logo?: boolean
  has_signature?: boolean
  project_count?: number
  pa_count?: number
}

export async function postAriaChat(
  messages: AriaChatMessage[],
  context?: AriaChatContext,
): Promise<AriaChatReply> {
  const body = await apiFetch<{ data: AriaChatReply; error?: string; message?: string }>(
    'POST',
    '/api/aria/chat',
    { messages, context: context || undefined },
  )
  if (!body.data) {
    throw new Error(body.message || body.error || 'Empty Soren reply')
  }
  const hasCard = !!(body.data.digest && body.data.digest.kind)
  if (!String(body.data.content || '').trim() && !hasCard) {
    throw new Error(body.message || body.error || 'Empty Soren reply')
  }
  return body.data
}

export async function sendOverdueFollowUp(payload: {
  owner_key: string
  confirm: boolean
}): Promise<{ sent?: boolean; to?: string; subject?: string; message?: string }> {
  const body = await apiFetch<{
    data: { sent?: boolean; to?: string; subject?: string; message?: string }
    error?: string
    message?: string
  }>('POST', '/api/aria/chat/send-overdue-followup', payload)
  if (!body.data) {
    throw new Error(body.message || body.error || 'Send failed')
  }
  return { ...body.data, message: body.message || body.data.message }
}

export async function executeAriaAction(payload: {
  action_id: number
  overrides?: Record<string, unknown>
  params?: Record<string, unknown> | null
}): Promise<AriaExecuteResult & { message?: string }> {
  const body = await apiFetch<{ data: AriaExecuteResult; error?: string; message?: string }>(
    'POST',
    '/api/aria/chat/execute-action',
    payload,
  )
  if (!body.data?.ok) {
    throw new Error(body.message || body.error || 'Action failed')
  }
  return { ...body.data, message: body.message || undefined }
}

export async function proposeAriaAction(payload: {
  action: 'draft_payment_followup' | 'set_cash_inputs'
  pay_app_ids?: number[]
  weekly_payroll_cents?: number | null
  cash_on_hand_cents?: number | null
  monthly_overhead_cents?: number | null
}): Promise<{ actions: AriaChatAction[]; more?: number; message?: string }> {
  const body = await apiFetch<{
    data: { actions: AriaChatAction[]; more?: number }
    error?: string
    message?: string
  }>('POST', '/api/aria/chat/propose-action', payload)
  if (!body.data) {
    throw new Error(body.message || body.error || 'Propose failed')
  }
  return {
    actions: Array.isArray(body.data.actions) ? body.data.actions : [],
    more: body.data.more || 0,
    message: body.message || undefined,
  }
}

export async function transcribeAriaAudio(
  blob: Blob,
  durationSec?: number,
): Promise<AriaTranscribeResult> {
  const form = new FormData()
  form.append('audio', blob, 'aria.webm')
  if (durationSec != null) form.append('duration_sec', String(durationSec))
  const token = getToken()
  const res = await fetch(resolveApiUrl('/api/aria/transcribe'), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok || !body.data?.text) {
    throw new Error(body.message || body.error || 'Transcription failed')
  }
  return body.data as AriaTranscribeResult
}
